import router, { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { Sheet, SheetRef } from 'react-modal-sheet';
import styled from 'styled-components';
import StepperIcon from '../../../../new-components/svg-icons/StepperIcon';
import AffordabilityFirstStep from './AffordabilityFirstStep';
import { useTranslation } from 'next-i18next';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import AffordabilityRange from './AffordabilityRangeStep';
import { logEvent } from '../../../../new-lib/analytics';
import { getAffordability, getCompounds } from '../../../../new-lib/apis';
import { SearchFiltersActionTypes } from '../../../../reducers/search-filters/actions';
import {
  SearchContext,
  SearchContextInterface,
  useSearchContext
} from '../../context/SearchContext';

interface AffordabilityStepperProps {
  stepper: boolean;
  setStepper: Dispatch<SetStateAction<boolean>>;
}
interface AffordabilityData {
  maxDownPayment: number;
  maxInstallment: number;
  minDownPayment: number;
  minInstallment: number;
}

const AffordabilityStepper = ({
  stepper,
  setStepper
}: AffordabilityStepperProps) => {
  const { locale } = useRouter();
  const { searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const { t } = useTranslation('search');
  const sheetRef = useRef<SheetRef>();
  const [customAreas, setCustomAreas] = useState<any>([]);
  const [customCompounds, setCustomCompounds] = useState<any>([]);
  const [downPayment, setDownPayment] = useState<any>(null);
  const [installment, setInstallment] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [compounds, setCompounds] = useState<any[]>([]);
  const [filteredCompounds, setFilteredCompounds] = useState<any>([]);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const { areas } = useSearchContext();

  const selectedAreas = areas
    .filter((area: any) => customAreas.includes(area.id))
    .map((area: any) => area.name);

  const selectedCompounds = compounds
    .filter(compound => customCompounds.includes(compound.id))
    .map(compound => compound.name);

  const [affordabilityData, setAffordabilityData] =
    useState<AffordabilityData | null>(null);

  useEffect(() => {
    const fetchCompounds = async () => {
      try {
        const response = await getCompounds(locale);
        setCompounds(response);
      } catch (error) {}
    };

    fetchCompounds();
  }, []);

  useEffect(() => {
    if (customAreas.length > 0) {
      const newFilteredCompounds = compounds.filter(
        (compound: any) =>
          customAreas.includes(compound.area_id) ||
          customAreas.includes(compound.parent_area_id)
      );

      const updatedCompounds = customCompounds.filter((customCompound: any) =>
        newFilteredCompounds.some(
          (filteredCompound: any) => filteredCompound.id === customCompound
        )
      );

      setFilteredCompounds(newFilteredCompounds);
      setCustomCompounds(updatedCompounds);
    } else {
      setFilteredCompounds([]);
      setCustomCompounds([]);
    }
  }, [customAreas, compounds]);

  const mapSelectedToQuery = () => {
    let queryArray = ['category=property'];
    if (customCompounds.length > 0) {
      const compounds = `${
        SearchFiltersTypes.COMPOUNDS
      }=${customCompounds.toString()}`;
      queryArray.push(compounds);
    }
    if (customAreas.length > 0 && customCompounds.length === 0) {
      const areas = `${SearchFiltersTypes.AREAS}=${customAreas.toString()}`;
      queryArray.push(areas);
    }
    queryArray.push(
      `${SearchFiltersTypes.DOWN_PAYMENT}=${
        downPayment ?? affordabilityData?.minDownPayment
      }`
    );
    queryArray.push(
      `${SearchFiltersTypes.MONTHLY_INSTALLMENTS}=${
        installment ?? affordabilityData?.minInstallment
      }`
    );

    return queryArray.join('&');
  };

  const goToResults = () => {
    setStepper(false);
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.RESET
    });
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
      payload: {
        name: SearchFiltersTypes.COMPOUNDS,
        value: []
      }
    });

    router.push({
      pathname: '/affordability-results',
      query: mapSelectedToQuery()
    });
    logEvent.click('AffCal_MP', 'search_page', 'Aff_Calculator');
  };

  const goToNextStep = async () => {
    setIsKeyboardOpen(false);
    if (currentStep === 1) {
      logEvent.click('AffCalc_location', 'search_page', 'Aff_Calculator', {
        areas: selectedAreas.length ? selectedAreas : null,
        cmpnds: selectedCompounds.length ? selectedCompounds : null
      });

      try {
        const requestBody = {
          areasIds: customAreas,
          compoundsIds: customCompounds
        };

        const response = await getAffordability(requestBody);

        if (response.maxDownPayment === 0 && response.maxInstallment === 0) {
          goToResults();
        }

        if (response) {
          setAffordabilityData({
            maxDownPayment: response.maxDownPayment,
            maxInstallment: response.maxInstallment,
            minDownPayment: response.minDownPayment,
            minInstallment: response.minInstallment
          });
        }
      } catch (error) {}
    } else {
      logEvent.click('AffCal_DP', 'search_page', 'Aff_Calculator');
    }
    setCurrentStep(prev => prev + 1);
  };

  const goToPrevStep = () => {
    setIsKeyboardOpen(false);
    setCurrentStep(prev => prev - 1);
  };

  const getSubtitle = () => {
    switch (currentStep) {
      case 1:
        return t('stepper_subtitle_first');
      case 2:
        return t('stepper_subtitle_second');
      case 3:
        return t('stepper_subtitle_last');
      default:
        return null;
    }
  };

  const secondStepTitle = (
    <>
      <span>{t('stepper_thirdq')}</span>{' '}
      <span className="highlighted">{t('stepper_thirdq_highlighted')}</span>{' '}
      <span>{t('stepper_thirdq_continued')}</span>{' '}
      <span className="highlighted">
        {t('stepper_thirdq_continued_highlighted')}
      </span>
    </>
  );

  const thirdStepTitle = (
    <>
      <span>{t('stepper_fourthq')}</span>{' '}
      <span className="highlighted">{t('stepper_fourthq_highlighted')}</span>
    </>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AffordabilityFirstStep
            areas={areas}
            compounds={filteredCompounds.length ? filteredCompounds : compounds}
            customAreas={customAreas}
            customCompounds={customCompounds}
            setCustomCompounds={setCustomCompounds}
            setCustomAreas={setCustomAreas}
          />
        );
      case 2:
        return (
          <AffordabilityRange
            stepTitle={secondStepTitle}
            filterType={SearchFiltersTypes.DOWN_PAYMENT}
            setDownPayment={setDownPayment}
            rangeStep={10000}
            rangeMin={affordabilityData?.minDownPayment ?? 0}
            rangeMax={affordabilityData?.maxDownPayment ?? 10000000}
            isKeyboardOpen={isKeyboardOpen}
            setIsKeyboardOpen={setIsKeyboardOpen}
          />
        );
      case 3:
        return (
          <AffordabilityRange
            stepTitle={thirdStepTitle}
            filterType={SearchFiltersTypes.MONTHLY_INSTALLMENTS}
            setInstallment={setInstallment}
            rangeStep={10000}
            rangeMin={affordabilityData?.minInstallment ?? 50000}
            rangeMax={affordabilityData?.maxInstallment ?? 10000000}
            isKeyboardOpen={isKeyboardOpen}
            setIsKeyboardOpen={setIsKeyboardOpen}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Sheet
      ref={sheetRef}
      isOpen={stepper}
      onClose={() => setStepper(false)}
      disableScrollLocking
    >
      <Sheet.Container>
        <Sheet.Header />
        <Sheet.Content
          style={{
            width: '100%',
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            direction: locale === 'ar' ? 'rtl' : 'ltr',
            padding: '8px 16px 24px 16px'
          }}
        >
          <Container locale={locale} className="hide-scrollbar">
            <div className="stepper-header">
              <StepperIcon />
              <div className="title-container">
                <span className="stepper-title">{t('stepper_title')}</span>
                <span className="stepper-subtitle">{getSubtitle()}</span>
              </div>

              <div className="steps-container">
                {[1, 2, 3].map(step => (
                  <div
                    key={step}
                    className="step-indicator"
                    style={{
                      backgroundColor:
                        step <= currentStep ? '#004D89' : '#D9D9D9'
                    }}
                  />
                ))}
              </div>
            </div>
            {renderStepContent()}

            <div className={`stepper-btn ${isKeyboardOpen && 'open-input'}`}>
              {currentStep > 1 && (
                <button className="back-btn" onClick={goToPrevStep}>
                  {t('back')}
                </button>
              )}
              {currentStep < 3 ? (
                <button onClick={goToNextStep}>{t('next')}</button>
              ) : (
                <button onClick={goToResults}>{t('find_home')}</button>
              )}
            </div>
          </Container>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onTap={() => setStepper(false)} />
    </Sheet>
  );
};

const Container = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;

  .stepper-header {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .title-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .stepper-title {
      color: #1a2028;
      text-align: center;
      font-family: materialBold;
      font-size: 21px;
      font-weight: 700;
    }

    .stepper-subtitle {
      color: #5b6573;
      text-align: center;
      font-family: materialRegular;
      font-size: 11px;
      font-weight: 400;
    }

    .steps-container {
      display: flex;
      justify-content: space-evenly;
      width: 100%;
      align-items: center;
      margin-bottom: 16px;

      .step-indicator {
        flex: 1;
        height: 8px;
        margin-right: 2px;
        margin-left: 2px;
        border-radius: 60px;
        transition: background-color 0.3s ease;
      }
    }
  }

  .stepper-btn {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    background-color: white;
    margin-top: 20px;
    gap: 16px;
    button {
      height: 50px;
      padding: 8px;
      width: 100%;
      justify-content: center;
      align-items: center;
      border-radius: 10px;
      background: #015c9a;
      color: white;
      margin-bottom: 0px;
      &.back-btn {
        background-color: white;
        color: #1a2028;
      }
    }
    &.open-input {
      margin-bottom: 320px;
    }
  }

  .step-question-container {
    width: 100%;
    padding: 24px 0px;

    button {
      margin-bottom: 0px;

      &.disable {
        background: rgb(241, 241, 241) !important;
      }
    }
  }
  .affordability-separator {
    width: 100%;
    height: 0.5px;
    opacity: 0.5;
    background: #d3dbe6;
  }

  .step-question {
    color: #1a2028;
    font-family: materialBold;
    font-size: 17px;
    font-weight: 700;
    align-self: flex-start;
    .highlighted {
      color: #ff5e00;
      font-family: materialBold;
      font-size: 17px;
      font-weight: 700;
    }
  }

  .slider-text {
    color: #5b6573;
    font-family: materialLight;
    padding-top: 8px;
    font-size: 11px;
    align-self: flex-start;
  }
  button {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    margin-bottom: 24px;
    padding: 10px 14px 10px 16px;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid #d3dbe6;
    background: #fff;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    color: #5b6573;
    font-family: materialMedium;
    font-size: 14px;
    svg {
      transform: rotate(180deg);
    }
  }

  .range-bar {
    display: flex;
    align-self: center;
    align-items: center;
    width: 85%;
    margin: 16px 0px;
    min-height: 48px;
    div[role='slider'] {
      background-color: #015c9a !important;
      width: 36px;
      height: 36px;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      border-radius: 50%;
      div {
        background-color: #fbfdff !important;
        border-radius: 50% !important;
        width: 18px !important;
        height: 18px !important;
        margin: 0 auto;
      }
    }
    .range-bar-bg {
      background-color: #e8f4ff !important;
      border-radius: 360px;
    }
  }

  .input-container {
    position: relative;
    display: inline-block;
    width: 95%;

    input {
      display: flex;
      width: 100%;
      padding: 8px;
      align-items: center;
      gap: 16px;
      border-radius: 8px;
      border: 1px solid #c6d0de;
      background: #fff;
    }
    span {
      position: absolute;
      ${props => props.locale === 'ar' && 'left: 8px'};
      ${props => props.locale === 'en' && 'right: 8px'};
      top: 50%;
      transform: translateY(-50%);
      color: #5b6573;
      font-family: materialMedium;
      font-size: 12px;
    }
  }
`;
export default AffordabilityStepper;
